import { useEffect } from "react";
import { DateInput, SelectInput, TextInput, Toast } from "../../Components";
import React, { useState } from "react";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  ArrowLeft,
} from "lucide-react";
import { dataCollectionService } from "../../Services/dataCollection.service";
import { useNavigate } from "react-router-dom";
import DateInputWrapper from "../../Components/Base/Form/DateInputWrapper";
import { getCache, setCache } from "../../Helpers/cachedata";
import { commonapiService } from "../../Services";

const selectPostType = [
  { id: "post", value: "Post" },
  { id: "reel", value: "Reel" },
  { id: "image", value: "Image" },
  { id: "video", value: "Video" },
];

const selectGender = [
  { id: "male", value: "Male" },
  { id: "female", value: "Female" },
  { id: "other", value: "Other" },
];

const selectCategory = [
  { id: "art", value: "Art" },
  { id: "fashion", value: "Fashion & Style" },
  { id: "beauty", value: "Beauty & Cosmetics" },
  { id: "fitness", value: "Fitness & Health" },
  { id: "food and cooking", value: "Food & Cooking" },
  { id: "travel", value: "Travel & Adventure" },
  { id: "lifestyle", value: "Lifestyle" },
  { id: "technology", value: "Technology" },
  { id: "gaming", value: "Gaming" },
  { id: "education", value: "Education" },
  { id: "business", value: "Business & Finance" },
];

const selectIsverify = [
  { id: "true", value: "True" },
  { id: "false", value: "False" },
];

const initialFormState = {
  user: {
    full_name: "",
    handle: "",
    bio: "",
    profile_url: "",
    followers_count: "",
    following_count: "",
    posts_count: "",
    followers_list: "",
    following_list: "",
    tagged_count: "",
    category: "",
    website: "",
    gender: "",
    country: "",
    date_joined: "",
  },
  posts: [
    {
      id: 1,
      likes_count: "",
      shortcode: "",
      caption: "",
      likes_list: "",
      comments_count: "",
      comments_dump: "",
      hashtags: "",
      media_type: "",
      posted_at: "",
    },
  ],
};

const initialPostSections = [{ id: 1 }];

export const ManualCreateDataCollection = () => {
  const [form, setForm] = useState(initialFormState);
  const [postSections, setPostSections] = useState(initialPostSections);
  const [expandedSections, setExpandedSections] = useState({
    user: true,
    post: true,
  });
  const [startDate, setStartDate] = useState(null);
  const [country, setCountry] = useState(null);
  const [errors, setErrors] = useState({});
  const [toastList, setToastList] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCountry();
  }, []);

  const fetchCountry = async () => {
    try {
      const cachedDataforCountry = getCache("country");
      if (cachedDataforCountry) {
        setCountry(cachedDataforCountry);
      } else {
        const res = await commonapiService.getCountry();
        if (res.data != null && res.success == true) {
          setCountry(res.data);
          setCache("country", res.data);
        }
      }
    } catch (err) {
      if (err.apiResponseStatus != 404) {
        addToast({
          error: true,
          msg: err.message,
          id: `${Date.now()}-${toastList.length}`,
        });
      }
    }
  };

  const selectCountryList = country
    ? country.map((country) => ({
        id: country.name, 
        value: country.name, 
      }))
    : [];


  const selectedCountryName = React.useMemo(() => {
    if (form.user.country && form.user.country !== "") {
      const foundCountry = selectCountryList.find(
        (item) => item.id === form.user.country
      );
      return foundCountry ? foundCountry.name : null;
    }
    return null;
  }, [form.user.country, selectCountryList]);

  const addToast = (data) => {
    const newToastList = [...toastList];
    if (data.msg) {
      if (data.msg[data.msg.length - 1] === ".")
        data.msg = data.msg.slice(0, -1);
    }
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  const addPostSection = () => {
    const newId = Date.now();
    setPostSections([...postSections, { id: newId }]);

    setForm((prev) => ({
      ...prev,
      posts: [
        ...prev.posts,
        {
          id: newId,
          likes_count: "",
          shortcode: "",
          caption: "",
          likes_list: "",
          comments_count: "",
          comments_dump: "",
          hashtags: "",
          media_type: "",
          posted_at: "",
        },
      ],
    }));
  };

  const removePostSection = (id) => {
    if (postSections.length > 1) {
      setPostSections(postSections.filter((section) => section.id !== id));

      setForm((prev) => ({
        ...prev,
        posts: prev.posts.filter((post) => post.id !== id),
      }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const resetForm = () => {
    setForm(initialFormState);
    setPostSections(initialPostSections);
    setErrors({});
    setStartDate(null);
  };

  const handleInputChange = (section, field, value, postId = null) => {
    setForm((prev) => {
      if (section === "posts" && postId) {
        return {
          ...prev,
          posts: prev.posts.map((post) =>
            post.id === postId ? { ...post, [field]: value } : post
          ),
        };
      } else {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        };
      }
    });

    // Clear error when user starts typing
    if (errors[field] || (postId && errors.posts?.[postId]?.[field])) {
      setErrors((prev) => {
        if (postId) {
          const postErrors = { ...prev.posts };
          if (postErrors[postId]) {
            delete postErrors[postId][field];
            if (Object.keys(postErrors[postId]).length === 0) {
              delete postErrors[postId];
            }
          }
          return {
            ...prev,
            posts: postErrors,
          };
        } else {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        }
      });
    }
  };

  // Handle text input changes with event object
  const handleTextInputChange =
    (section, field, postId = null) =>
    (e) => {
      handleInputChange(section, field, e.target.value, postId);
    };

  // Handle select changes
  const handleSelectChange =
    (section, field, postId = null) =>
    (value) => {
      handleInputChange(section, field, value, postId);
    };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Validate user fields
    if (!form.user.handle.trim()) newErrors.handle = "Please enter handle";
    // if (!form.user.followers_count) newErrors.followers_count = "Please enter followers";
    // if (!form.user.following_count) newErrors.following_count = "Please enter following";
    if (!form.user.profile_url)
      newErrors.profile_url = "Please enter profile url ";

    // Validate post fields
    form.posts.forEach((post) => {
      if (!post.media_type) {
        if (!newErrors.posts) newErrors.posts = {};
        if (!newErrors.posts[post.id]) newErrors.posts[post.id] = {};
        newErrors.posts[post.id].media_type = "Please select Post Type";
      }
      if (!post.shortcode) {
        if (!newErrors.posts) newErrors.posts = {};
        if (!newErrors.posts[post.id]) newErrors.posts[post.id] = {};
        newErrors.posts[post.id].shortcode = "Please enter shortcode";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveCollectionData = async () => {
    try {
      if (validateForm()) {
        // Find country name from ID
        const countryName = form.user.country
          ? selectCountryList.find((item) => item.id === form.user.country)
              ?.value
          : "";

        const apiData = {
          user: {
            handle: form.user.handle,
            full_name: form.user.full_name,
            bio: form.user.bio,
            profile_url: form.user.profile_url,
            followers_count: parseInt(form.user.followers_count) || 0,
            following_count: parseInt(form.user.following_count) || 0,
            posts_count: parseInt(form.user.posts_count) || 0,
            tagged_count: parseInt(form.user.tagged_count) || 0,
            category: form.user.category,
            website: form.user.website,
            followers_list: form.user.followers_list,
            following_list: form.user.following_list,
            country: countryName, 
            date_joined: form.user.date_joined,
            gender: form.user.gender,
            profile_image_url: form.user.profile_image_url,
            verified: form.user.verified,
          },
          posts: form.posts.map((post) => ({
            id:
              post.shortcode ||
              `instagram_${Math.random().toString(36).substr(2, 9)}`,
            shortcode: post.shortcode,
            caption: post.caption,
            likes_count: parseInt(post.likes_count) || 0,
            likes_list: post.likes_list,
            comments_count: parseInt(post.comments_count) || 0,
            comments_dump: post.comments_dump,
            media_type: post.media_type,
            hashtags: post.hashtags,
            post_url: post.post_url,
            posted_at_raw: post.posted_at
              ? new Date(post.posted_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "",
            posted_at: post.posted_at || new Date().toISOString().split("T")[0],
          })),
        };

        console.log("API Data:", apiData);

        const res = await dataCollectionService.saveCollectionData(apiData);
        if (res && res.success === true) {
          console.log("Response Data:", res);
          alert("Data saved successfully!");
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving data. Please try again.");
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const SectionHeader = ({
    title,
    sectionKey,
    onAdd,
    showAddButton = false,
  }) => (
    <div
      className="flex items-center justify-between mb-6 p-4 bg-white rounded-lg border border-emerald-100 shadow-sm cursor-pointer hover:bg-emerald-50 transition-colors duration-200"
      onClick={() => toggleSection(sectionKey)}
    >
      <div className="flex items-center">
        <div className="w-2 h-8 bg-gradient-to-b from-emerald-400 to-green-500 rounded-full mr-4 shadow-sm"></div>
        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
      </div>
      <div className="flex items-center gap-3">
        {showAddButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200 shadow-sm hover:shadow-md"
          >
            <Plus size={20} />
          </button>
        )}
        <div className="text-emerald-600">
          {expandedSections[sectionKey] ? (
            <ChevronUp size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-full mx-auto px-4 py-4 space-y-4">
      <div className="flex justify-start mb-0">
        <button
          className="w-full sm:w-auto order-2 sm:order-1 px-4 py-2 bg-white border border-emerald-100 rounded-lg hover:bg-emerald-50 hover:border-emerald-400 transition-all duration-200 font-medium shadow-sm flex justify-start"
          onClick={goBack}
        >
          <ArrowLeft color="#374151" />{" "}
          <span className="text-gray-700 ml-1">Back</span>
        </button>
      </div>

      {/* User Data Section */}
      <div className="rounded-2xl bg-white border border-emerald-100 shadow-sm hover:shadow-md transition-shadow duration-300">
        <SectionHeader title="User Data" sectionKey="user" />

        {expandedSections.user && (
          <div className="p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <TextInput
                type="text"
                label="Name"
                placeholder="Please enter full name"
                value={form.user.full_name}
                onChange={handleTextInputChange("user", "full_name")}
                error={errors.full_name}
                errorText={errors.full_name || "Please enter full name"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <TextInput
                type="text"
                label="Bio"
                placeholder="Please enter bio"
                value={form.user.bio}
                onChange={handleTextInputChange("user", "bio")}
                error={errors.bio}
                errorText={errors.bio || "Please enter bio"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <TextInput
                type="text"
                label="Profile URL"
                placeholder="Please enter profile url"
                value={form.user.profile_url}
                onChange={handleTextInputChange("user", "profile_url")}
                error={errors.profile_url}
                errorText={errors.profile_url || "Please enter profile url"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                isRequired
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <TextInput
                type="text"
                label="Handle"
                placeholder="Please enter handle"
                value={form.user.handle}
                onChange={handleTextInputChange("user", "handle")}
                error={errors.handle}
                errorText={errors.handle || "Please enter profile url"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                isRequired
              />
              <TextInput
                type="text"
                label="Profile Image URL"
                placeholder="Please enter profile image url"
                value={form.user.profile_image_url}
                onChange={handleTextInputChange("user", "profile_image_url")}
                error={errors.profile_image_url}
                errorText={
                  errors.profile_image_url || "Please enter profile image url"
                }
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <SelectInput
                label="Verified"
                list={selectIsverify}
                value={form.user.verified}
                onChange={handleSelectChange("user", "verified")}
                error={errors.verified}
                errorText={errors.verified || "Please select verified"}
                className={`border !border-emerald-200 ${
                  errors.verified ? "border-red-500" : ""
                }`}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              <TextInput
                type="number"
                label="Followers#"
                value={form.user.followers_count}
                onChange={handleTextInputChange("user", "followers_count")}
                error={errors.followers_count}
                placeholder="Please enter Followers"
                errorText={errors.followers_count || "Please enter Followers"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <TextInput
                type="number"
                label="Following#"
                value={form.user.following_count}
                onChange={handleTextInputChange("user", "following_count")}
                error={errors.following_count}
                placeholder="Please enter Following"
                errorText={errors.following_count || "Please enter Following"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <TextInput
                type="number"
                label="Number of Total Posts#"
                value={form.user.posts_count}
                onChange={handleTextInputChange("user", "posts_count")}
                error={errors.posts_count}
                placeholder="Please enter the total number of posts"
                errorText={errors.posts_count || "Please enter total posts"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              <TextInput
                type="number"
                label="Number of Total Mentions#"
                value={form.user.tagged_count}
                onChange={handleTextInputChange("user", "tagged_count")}
                error={errors.tagged_count}
                placeholder="Please enter the total number of Mentions"
                errorText={errors.tagged_count || "Please enter Mentions"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
              <SelectInput
                label="Category"
                list={selectCategory}
                value={form.user.category}
                onChange={handleSelectChange("user", "category")}
                error={errors.category}
                errorText={errors.category || "Please select Category"}
                className={`border !border-emerald-200 ${
                  errors.category ? "border-red-500" : ""
                }`}
              />
              <TextInput
                type="text"
                label="Website"
                value={form.user.website}
                onChange={handleTextInputChange("user", "website")}
                error={errors.website}
                placeholder="Please enter website"
                errorText={errors.website || "Please enter website"}
                className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              <SelectInput
                label="Gender"
                list={selectGender}
                value={form.user.gender}
                onChange={handleSelectChange("user", "gender")}
                error={errors.gender}
                errorText={errors.gender || "Please select Gender"}
                className={`border !border-emerald-200 ${
                  errors.gender ? "border-red-500" : ""
                }`}
              />
              <SelectInput
                label="Country"
                value={form.user.country}
                list={selectCountryList}
                onChange={handleSelectChange("user", "country")}
                error={errors.country}
                errorText="Please select Country"
                className={`border !border-emerald-200 ${
                  errors.country ? "border-red-500" : ""
                }`}
                // isRequired
              />
              <DateInputWrapper
                label="Date Joined"
                startDate={
                  form.user.date_joined ? new Date(form.user.date_joined) : null
                }
                onChange={(date) => {
                  const dateValue = date
                    ? date.toISOString().split("T")[0]
                    : "";
                  handleInputChange("user", "date_joined", dateValue);
                }}
                placeholder="Choose a date"
                error={errors.date_joined}
                errorText={errors.date_joined || "Date joined is required"}
                icon={<CalendarDays color="#9ca3af" size={18} />}
                className={`border !border-emerald-200 rounded-lg ${
                  errors.date_joined ? "border-red-500" : ""
                }`}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              <div>
                <label className="flex mb-1 items-center gap-2 text-sm font-bold">
                  List of followers
                </label>
                <textarea
                  value={form.user.followers_list}
                  onChange={handleTextInputChange("user", "followers_list")}
                  placeholder="Please enter Followers list"
                  rows={3}
                  maxLength={1000}
                  className="w-full p-3 border border-emerald-200 rounded-lg focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 transition-colors resize-vertical min-h-[100px] text-gray-900 placeholder-gray-400"
                />
              </div>

              <div>
                <label className="flex mb-1 items-center gap-2 text-sm font-bold">
                  List of following
                </label>
                <textarea
                  value={form.user.following_list}
                  onChange={handleTextInputChange("user", "following_list")}
                  placeholder="Please enter following list"
                  rows={3}
                  maxLength={1000}
                  className="w-full p-3 border border-emerald-200 rounded-lg focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 transition-colors resize-vertical min-h-[100px] text-gray-900 placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm hover:shadow-md transition-shadow duration-300">
        <SectionHeader
          title="Post Data"
          sectionKey="post"
          onAdd={addPostSection}
          showAddButton={true}
        />

        {expandedSections.post && (
          <div className="p-6 pt-0 space-y-6">
            {form.posts.map((post, index) => (
              <div
                key={post.id}
                className="relative p-6 rounded-xl border border-emerald-200/60"
              >
                {form.posts.length > 1 && (
                  <button
                    onClick={() => removePostSection(post.id)}
                    className="absolute top-4 right-4 p-2 text-red-400 hover:text-red-600 transition-colors duration-200"
                  >
                    <Trash2 size={16} />
                  </button>
                )}

                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mr-3"></div>
                  <h4 className="font-semibold text-gray-700">
                    Post {index + 1}
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  <TextInput
                    type="text"
                    label="Short Code"
                    value={post.shortcode}
                    onChange={handleTextInputChange(
                      "posts",
                      "shortcode",
                      post.id
                    )}
                    error={errors.posts?.[post.id]?.shortcode}
                    errorText={
                      errors.posts?.[post.id]?.shortcode ||
                      "Please enter shortcode"
                    }
                    placeholder="Please enter shortcode"
                    className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                    isRequired
                  />
                  <TextInput
                    type="text"
                    label="Caption"
                    value={post.caption}
                    onChange={handleTextInputChange(
                      "posts",
                      "caption",
                      post.id
                    )}
                    error={errors.posts?.[post.id]?.caption}
                    errorText={
                      errors.posts?.[post.id]?.caption || "Please enter caption"
                    }
                    placeholder="Please enter caption"
                    className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  />
                  <TextInput
                    type="number"
                    label="Likes Count#"
                    value={post.likes_count}
                    onChange={handleTextInputChange(
                      "posts",
                      "likes_count",
                      post.id
                    )}
                    placeholder="Please enter number of Likes"
                    error={errors.posts?.[post.id]?.likes_count}
                    errorText={
                      errors.posts?.[post.id]?.likes_count ||
                      "Please enter likes"
                    }
                    className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  />
                  <TextInput
                    type="number"
                    label="No. of Comments#"
                    value={post.comments_count}
                    onChange={handleTextInputChange(
                      "posts",
                      "comments_count",
                      post.id
                    )}
                    placeholder="Please enter number of comments"
                    error={errors.posts?.[post.id]?.comments_count}
                    errorText={
                      errors.posts?.[post.id]?.comments_count ||
                      "Please enter number of comments"
                    }
                    className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  />
                  <SelectInput
                    label="Post Type"
                    list={selectPostType}
                    value={post.media_type}
                    onChange={handleSelectChange(
                      "posts",
                      "media_type",
                      post.id
                    )}
                    error={errors.posts?.[post.id]?.media_type}
                    errorText={
                      errors.posts?.[post.id]?.media_type ||
                      "Please select PostType"
                    }
                    isRequired
                    className={`border !border-emerald-200 ${
                      errors.posts?.[post.id]?.media_type
                        ? "border-red-500"
                        : ""
                    }`}
                  />
                  <DateInputWrapper
                    label="Posted At"
                    value={post.posted_at}
                    onChange={(date) =>
                      handleInputChange("posts", "posted_at", date, post.id)
                    }
                    placeholder="Choose a date"
                    error={errors.posts?.[post.id]?.posted_at}
                    errorText={
                      errors.posts?.[post.id]?.posted_at ||
                      "Posted date is required"
                    }
                    icon={<CalendarDays color="#9ca3af" size={18} />}
                    className={`border !border-emerald-200 rounded-lg ${
                      errors.date_joined ? "border-red-500" : ""
                    }`}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div>
                    <label className="flex mb-1 items-center gap-2 text-sm font-bold">
                      Hashtags
                    </label>
                    <textarea
                      value={post.hashtags}
                      onChange={handleTextInputChange(
                        "posts",
                        "hashtags",
                        post.id
                      )}
                      placeholder="Please enter hashtags"
                      rows={3}
                      maxLength={1000}
                      className="w-full p-3 border border-emerald-200 rounded-lg focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 transition-colors resize-vertical min-h-[100px] text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="flex mb-1 items-center gap-2 text-sm font-bold">
                      Comments
                    </label>
                    <textarea
                      value={post.comments_dump}
                      onChange={handleTextInputChange(
                        "posts",
                        "comments_dump",
                        post.id
                      )}
                      rows={3}
                      maxLength={1000}
                      placeholder="Please enter comments dump"
                      className="w-full p-3 border border-emerald-200 rounded-lg focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 transition-colors resize-vertical min-h-[100px] text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="flex mb-1 items-center gap-2 text-sm font-bold">
                      Likes List
                    </label>
                    <textarea
                      value={post.likes_list}
                      onChange={handleTextInputChange(
                        "posts",
                        "likes_list",
                        post.id
                      )}
                      rows={3}
                      maxLength={1000}
                      placeholder="Please enter likes list"
                      className="w-full p-3 border border-emerald-200 rounded-lg focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 transition-colors resize-vertical min-h-[100px] text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <TextInput
                    type="text"
                    label="Post URL"
                    value={post.post_url}
                    onChange={handleTextInputChange(
                      "posts",
                      "post_url",
                      post.id
                    )}
                    placeholder="Please enter post url"
                    error={errors.posts?.[post.id]?.post_url}
                    errorText={
                      errors.posts?.[post.id]?.post_url ||
                      "Please enter post url"
                    }
                    className="!border !border-emerald-200 focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
          onClick={saveCollectionData}
        >
          Save Data Collection
        </button>
      </div>
      {toastList && <Toast list={toastList} setList={setToastList} />}
    </div>
  );
};
